import React, { useEffect, useMemo, useRef, useState } from 'react'
import { message, Tooltip , Upload } from 'antd'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import pluralize from 'pluralize'
import CreatableSelect from 'react-select/creatable'
import * as ECT from '@whoicd/icd11ect'

import { AiOutlineDelete, AiOutlinePlus } from 'react-icons/ai'

import { icdRelease, icdServer, server, stringifyDate, useListData, usePet, useToggle } from '../../modules'

import Datepicker from "../reusable/datePicker"
import ImagePreview from './imagePreview'
import Elevated from '../modals/elevated'
import Confirm from './confirm'
import '@whoicd/icd11ect/style.css'

const diagnosisToolId = (fieldName, multi) => `form_icd11_${String(fieldName ?? 'field').replace(/[^a-zA-Z0-9]/g, '_')}_${multi ? 'multi' : 'single'}`

// ECT.Handler.configure() is a global singleton — calling it more than once replaces
// the previous selectedEntityFunction entirely rather than adding to it. Every
// Icd11Searchable/Icd11ConceptPicker instance on the page (e.g. primary + secondary
// diagnosis, both mounted together) used to call configure() itself, so whichever
// instance mounted last silently owned all future selections and every other
// instance's clicks became no-ops. Configure exactly once, and dispatch to whichever
// instance's own handler is currently registered for the reported tool id.
const icd11Handlers = new Map()
let icd11Configured = false
const ensureIcd11Configured = () => {
    if (icd11Configured) return
    icd11Configured = true
    ECT.Handler.configure({
        apiServerUrl: icdServer,
        apiSecured: false,
        autoBind: false,
        popupMode: false,
        searchByCodeOrURI: true,
        language: 'en',
    }, {
        selectedEntityFunction: selectedEntity => {
            const selectedTool = String(selectedEntity?.iNo ?? '')
            icd11Handlers.get(selectedTool)?.(selectedEntity)
        },
    })
}

export const Searchable = ({ multi, disabled, template, onChange, onBlur, value, placeholder = "Please type to search" }) => {
    const [val, setVal] = useState(multi ? value?.map(vl => ({ label: vl, value: vl })) : value?.value ? { label: value.value[0], value: value.value[0] } : null)
    const [string, setString] = useState('')

    const [{ loading: dLoading, fetch }, { list = [] }] = useListData(template?.endpoint, { string }, { notify: true, manual: template === undefined }, template?.endpoint, template?.limit ?? 20)

    const search = str => {
        if (str?.trim()?.length > 2) {
            setString(str)
            fetch()
        }
    }

    return (
        <Select
            isMulti={multi}
            className="mb-3"
            isDisabled={ disabled }
            isLoading={dLoading}
            loadingMessage={str => `Searching for "${str?.inputValue}"`}
            onInputChange={search}
            value={val}
            onBlur={onBlur}
            onChange={vals => {
                setVal(vals)
                onChange(multi ? vals.map(vl => vl.label) : vals.label)
            }}
            options={list.map(item => ({ label: item.name, value: item._id, ...item }))}
            placeholder={placeholder} />
    )
}

export const SearchableCreatable = ({ multi, disabled, template, onChange, onBlur, value, placeholder = "Please type to search" }) => {
    const [val, setVal] = useState(multi ? value?.map(vl => ({ label: vl, value: vl })) : value?.value ? { label: value.value[0], value: value.value[0] } : null)
    const [string, setString] = useState('')
    const [createStr, setCreateStr] = useState('')

    const [{ loading: dLoading, fetch }, { list = [] }] = useListData(template?.endpoint, { string }, { notify: true, manual: template === undefined }, template?.endpoint, template?.limit ?? 20)

    const onComplete = ({ data }) => {
        let nw = { label: data.name, value: data.name }

        let vls = multi ? [...val, nw] : nw
        
        setVal(vls)

        onChange(multi ? vls.map(vl => vl.label) : vls.label)
    }

    const [{ loading: cLoading }, create] = usePet(template?.createEndpoint, { name: createStr }, { notify: true, manual: true, callback: onComplete, load: { msg: 'Adding new option...' } })

    const search = str => {
        if (str?.trim()?.length > 2) {
            setString(str)
            fetch()
        }
    }

    return (
        <CreatableSelect
            isMulti={multi}
            className="mb-3"
            isDisabled={ disabled }
            isLoading={dLoading || cLoading}
            loadingMessage={str => `Searching for "${str?.inputValue}"`}
            onInputChange={search}
            value={val}
            onBlur={onBlur}
            onCreateOption={vl => {
                setCreateStr(vl)
                create()
            }}
            onChange={vals => {
                setVal(vals)
                onChange(multi ? vals.map(vl => vl.label) : vals.label)
            }}
            options={list.map(item => ({ label: item.name, value: item._id, ...item }))}
            placeholder={placeholder} />
    )
}

export const SearchableFromPrevious = ({ multi, disabled, template, onChange, onBlur, value, placeholder = "Please type to search" }) => {
    const [vals, setVals] = useState([])
    const [val, setVal] = useState(multi ? value?.map(vl => ({ label: vl, value: vl })) : value?.value ? { label: value.value[0], value: value.value[0] } : null)
    const [string, setString] = useState('')

    let payload = template.payload ? template.payload : {}

    const onComplete = ({ data }) => {
        data = [...data, ...vals]
        data = new Set(data)
        setVals([...data])
    }

    const [{ loading: dLoading }, getValues] = usePet(template?.endpoint ?? 'previousValues', { string, ...payload }, { notify: true, manual: template === undefined, callback: onComplete })

    const search = str => {
        if (str?.trim()?.length >= 2 || str?.trim()?.length === 0) {
            setString(str)
            getValues()
        }
    }

    return (
        <CreatableSelect
            isMulti={multi}
            className="mb-3"
            isDisabled={ disabled }
            isLoading={dLoading}
            loadingMessage={str => `Searching for "${str?.inputValue}"`}
            onInputChange={search}
            value={val}
            onBlur={onBlur}
            onCreateOption={vl => {
                setVals([...vals, vl])
                let v = { label: vl, value: vl }
                setVal(multi ? [v] : v)
                setTimeout(() => onChange(multi ? [...vals, vl] : vl), 100)
            }}
            onChange={vls => {
                setVal(vls)
                onChange(multi ? vls.map(vl => vl.label) : vls.label)
            }}
            options={vals.map(item => ({ label: item, value: item }))}
            placeholder={placeholder} />
    )
}

export const Icd11Searchable = ({ multi, disabled, field, value, onChange, onCommit }) => {
    const [selected, setSelected] = useState(() => {
        if (multi)
            return Array.isArray(value) ? value : []

        return value ?? ''
    })
    // The ECT widget owns this input's typing/dropdown behavior via direct DOM manipulation
    // (it's deliberately uncontrolled, no value/onChange) - it never writes the final pick
    // back into the box, and the results panel below stays open after a selection. Both are
    // fixed here rather than in the widget: set .value directly the same way ECT's own
    // internal forceSearch() does, and hide the panel with our own CSS state until the user
    // interacts with the box again.
    const inputRef = useRef(null)
    const [resultsOpen, setResultsOpen] = useState(true)
    const toolId = useMemo(() => diagnosisToolId(field?.name, multi), [field?.name, multi])

    useEffect(() => {
        ensureIcd11Configured()
        icd11Handlers.set(toolId, selectedEntity => {
            const code = selectedEntity?.theCode ?? selectedEntity?.code
            const title = selectedEntity?.title
            const nextValue = `${code} - ${title}`

            ECT.Handler.clear(toolId)

            if (multi) {
                setSelected(current => {
                    const nextItems = Array.from(new Set([...(Array.isArray(current) ? current : []), nextValue]))
                    onChange(nextItems)
                    return nextItems
                })
                if (inputRef.current) inputRef.current.value = ''
            } else {
                setSelected(nextValue)
                onChange(nextValue)
            }

            setResultsOpen(false)
            setTimeout(onCommit, 0)
        })

        return () => icd11Handlers.delete(toolId)
    }, [multi, onChange, onCommit, toolId])

    useEffect(() => {
        const timer = setTimeout(() => {
            ECT.Handler.bind(toolId)
        }, 100)

        return () => clearTimeout(timer)
    }, [toolId])

    useEffect(() => {
        if (multi)
            setSelected(Array.isArray(value) ? value : [])
        else
            setSelected(value ?? '')
    }, [multi, value])

    // Keeps the searchbox showing the current single-select pick regardless of how `selected`
    // changed (fresh pick above, initial mount with an already-saved value, or an external
    // reset) - one place instead of setting .value at every call site that can change it.
    useEffect(() => {
        if (multi || !inputRef.current) return
        inputRef.current.value = selected || ''
    }, [multi, selected])

    return (
        <div className="mb-3">
            <input
                ref={ inputRef }
                type="text"
                autoComplete="off"
                disabled={ disabled }
                className="ctw-input embeddedBrowserSearchbox apiInitializationCompleted appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4"
                data-ctw-ino={toolId}
                placeholder="Search ICD-11 diagnoses"
                onFocus={ () => setResultsOpen(true) }
            />
            <div className="ctw-window mt-2 mb-2" data-ctw-ino={toolId} style={ resultsOpen ? undefined : { display: 'none' } } />
            {
                multi ?
                    <div className="flex flex-wrap gap-2">
                        {
                            selected.length > 0 ?
                                selected.map(item => (
                                    <span key={ item } className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs">
                                        { item }
                                        {
                                            !disabled &&
                                            <button
                                                type="button"
                                                className="ml-2"
                                                onClick={ () => {
                                                    const nextItems = selected.filter(entry => entry !== item)
                                                    setSelected(nextItems)
                                                    onChange(nextItems)
                                                    setTimeout(onCommit, 0)
                                                } }>
                                                ×
                                            </button>
                                        }
                                    </span>
                                ))
                                : <p className="text-gray-500 text-xs italic mb-0">No ICD-11 diagnoses selected yet.</p>
                        }
                    </div>
                :
                    <p className="text-gray-500 text-xs italic mb-0">Selected diagnosis: { selected || 'None' }</p>
            }
        </div>
    )
}

export const Icd11ConceptPicker = ({ multi = false, disabled, toolId, value, onChange, placeholder = "Search ICD-11 diagnoses" }) => {
    const onChangeRef = useRef(onChange)
    const [selected, setSelected] = useState(() => {
        if (multi)
            return Array.isArray(value) ? value : []

        return value ?? null
    })
    // Same fix as Icd11Searchable above: ECT's uncontrolled input never shows the final pick,
    // and its results panel stays open after a selection - both are handled here instead.
    const inputRef = useRef(null)
    const [resultsOpen, setResultsOpen] = useState(true)

    useEffect(() => {
        onChangeRef.current = onChange
    }, [onChange, onChangeRef])

    useEffect(() => {
        ensureIcd11Configured()
        icd11Handlers.set(toolId, selectedEntity => {
            const concept = {
                code: selectedEntity?.theCode ?? selectedEntity?.code,
                title: selectedEntity?.title,
                uri: selectedEntity?.chapterOrBlockURI ?? selectedEntity?.stemURI ?? selectedEntity?.uri,
                system: 'ICD-11',
                release: icdRelease,
            }

            if (!concept.code || !concept.title) return

            ECT.Handler.clear(toolId)

            if (multi) {
                setSelected(current => {
                    const existing = Array.isArray(current) ? current : []
                    const nextItems = existing.some(item => item?.code === concept.code)
                        ? existing
                        : [...existing, concept]

                    onChangeRef.current(nextItems)
                    return nextItems
                })
                if (inputRef.current) inputRef.current.value = ''
            } else {
                setSelected(concept)
                onChangeRef.current(concept)
            }

            setResultsOpen(false)
        })

        return () => icd11Handlers.delete(toolId)
    }, [multi, onChangeRef, toolId])

    useEffect(() => {
        const timer = setTimeout(() => {
            ECT.Handler.bind(toolId)
        }, 100)

        return () => clearTimeout(timer)
    }, [toolId])

    useEffect(() => {
        if (multi)
            setSelected(Array.isArray(value) ? value : [])
        else
            setSelected(value ?? null)
    }, [multi, value])

    // Keeps the searchbox showing the current single-select pick regardless of how `selected`
    // changed (fresh pick above, initial mount with an already-saved diagnosis, or an external
    // reset) - one place instead of setting .value at every call site that can change it.
    useEffect(() => {
        if (multi || !inputRef.current) return
        inputRef.current.value = selected ? `${selected.code} - ${selected.title}` : ''
    }, [multi, selected])

    return (
        <div className="mb-3">
            <input
                ref={ inputRef }
                type="text"
                autoComplete="off"
                disabled={ disabled }
                className="ctw-input embeddedBrowserSearchbox apiInitializationCompleted appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4"
                data-ctw-ino={toolId}
                placeholder={placeholder}
                onFocus={ () => setResultsOpen(true) }
            />
            <div className="ctw-window mt-2 mb-2" data-ctw-ino={toolId} style={ resultsOpen ? undefined : { display: 'none' } } />
            {
                multi ?
                    <div className="flex flex-wrap gap-2">
                        {
                            selected?.length > 0 ?
                                selected.map(item => (
                                    <span key={ item?.code } className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs">
                                        { item?.code } - { item?.title }
                                        {
                                            !disabled &&
                                            <button
                                                type="button"
                                                className="ml-2"
                                                onClick={ () => {
                                                    const nextItems = selected.filter(entry => entry?.code !== item?.code)
                                                    setSelected(nextItems)
                                                    onChangeRef.current(nextItems)
                                                } }>
                                                ×
                                            </button>
                                        }
                                    </span>
                                ))
                                : <p className="text-gray-500 text-xs italic mb-0">No ICD-11 diagnoses selected yet.</p>
                        }
                    </div>
                :
                    <p className="text-gray-500 text-xs italic mb-0">Selected diagnosis: { selected ? `${selected?.code} - ${selected?.title}` : 'None' }</p>
            }
        </div>
    )
}

export const MultiFileUpload = ({ multi, entry, directory, field, disabled, onChange, onBlur, value = [] }) => {
    const [name, setName] = useState('')
    const [list, setFileList] = useState([])
    const [previews, setPreviews] = useState(value.map((x, i) => ({
        uid: i + 1,
        name: x,
        status: 'done',
        url: `${server}/uploads/${directory}/${x}`
    })))

    const [previewVisible, setPreview] = useState(false)
    const [previewImage, setPreviewImage] = useState(false)
    const [previewTitle, setPreviewTitle] = useState(false)
    const [token, setToken] = useState()
    const [onSuccess, setOnSuccess] = useState(() => null)

    const [elevated, toggleElevated] = useToggle()

    const rerender = ent => {
        let newVal = ent?.fields?.find(fld => fld.name === field.name)?.value
        setFileList([])
        setPreviews(newVal.map((x, i) => ({
            uid: i + 1,
            name: x,
            status: 'done',
            url: `${server}/uploads/${directory}/${x}`
        })))
    }

    const [{ loading }, remove] = usePet('removeSingleValueFromEntry', {
        id: entry?._id,
        directory,
        field: field?.name,
        value: name
    }, {
        notify: true, callback: ({ data }) => {
            message.success('Item has been removed')
            rerender(data)
        }, manual: true, load: { msg: 'Removing item...' }, token
    })

    const [{ loading: fllLoading }, fillEntry] = usePet('fillEntryMultiple', {
        id: entry?._id,
        directory,
        field: field?.name,
        value: list
    }, {
        notify: true, hasFile: true, callback: ({ data }) => {
            message.success('Changes saved')
            rerender(data)
        }, manual: true, load: { msg: 'Saving changes...' }
    })

    const handleCancel = () => setPreview(false)

    const handleChange = ({ fileList }) => {
        setPreviews(fileList)
        console.log({ list, previews })
        return false
    }

    const handlePreview = async file => {
        if (!file.url && !file.preview)
            file.preview = await getBase64(file.originFileObj)

        setPreview(true)
        setPreviewImage(file.url || file.preview)
        setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1))
    }

    const beforeUpload = (_, files) => {
        let newList = [...list, ...files]
        setFileList(newList)
        setTimeout(fillEntry, 200)
        return false
    }

    const onRemove = file => {
        const index = list.indexOf(file)

        if (isNaN(file.uid)) {
            const newFileList = list.slice()
            newFileList.splice(index, 1)
            setFileList(newFileList)
        } else {
            setName(file.name)
            remove()
        }

        const newPList = previews.slice()
        newPList.splice(index)
        setPreviews(newPList)
    }

    const getBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    })

    const uploadButton = (
        <div className="flex flex-col justify-center items-center">
            <AiOutlinePlus />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    )

    return (
        <>
            {
                elevated &&
                <Elevated
                    onSuccess={onSuccess}
                    toggle={toggleElevated}
                    visible={elevated} />
            }
            <ImagePreview
                visible={previewVisible}
                image={previewImage}
                toggle={handleCancel} />
            <Upload
                action={`${server}/images`}
                multiple={multi}
                maxCount={8}
                disabled={ disabled }
                fileList={previews}
                listType={ field.type === 'MultiFileUpload' ? undefined : "picture-card" }
                beforeUpload={beforeUpload}
                showUploadList={
                    field.type === 'MultiFileUpload' ?
                    {
                        showDownloadIcon: true,
                        downloadIcon: 'Download',
                        showRemoveIcon: true
                    } : undefined
                }
                onPreview={ field.type === "MultiFileUpload" ? () => {
                    for (let prev of previews)
                        window.open(prev.url, '_blank').focus() 
                } : handlePreview() }
                onRemove={file => {
                    setOnSuccess({ fn: () => onRemove(file), setToken })
                    setTimeout(toggleElevated, 200)
                }}
                onChange={handleChange}>
                {list.length >= 8 ? null : uploadButton}
            </Upload>
        </>
    )
}

export const EntryField = ({ entry, field, reload = () => null }) => {
    let directory = pluralize(entry?.item?.name ?? entry?.ref?.name)?.toLowerCase()
    let template = entry?.item?.fields?.find(fld => fld.name === field.name)
    let currFld = entry?.fields?.find(fld => fld.name === field.name)
    let currentVal = currFld?.value
    
    const { user } = useSelector(state => state.application)
    
    const [prev, setPrev] = useState(
        template?.dataSourceKey === 'icd11_diagnoses'
            ? (template?.type === 'MultiSearchableSelect' ? currentVal ?? [] : currentVal?.[0] ?? '')
            : template?.type === 'Select'
                ? currentVal ? currentVal[0] : null
                : (template?.type === 'SearchableSelect' || template?.type === 'SearchableFromPrevious')
                    ? currentVal ? { value: currentVal } : null
                    : template?.type === 'Date'
                        ? currentVal ? currentVal[0] : undefined
                        : currentVal
    )
    const [value, setVal] = useState(prev)
    const [index, setIndex] = useState()
    const [filled, setFilled] = useState(entry?.fields?.filter(fld => fld.name === field.name)?.map((fld, i) => ({ index: i, ...fld})))
    const [commitVersion, setCommitVersion] = useState(0)
    
    const [{ loading: fllLoading }, fillEntry] = usePet((template?.type === 'MultiSearchableSelect' || template?.type === 'MultiSearchableFromPrevious' || template?.type === 'MultiImageUpload' || template?.type === 'MultiSearchableCreatableSelect') ? 'fillEntryMultiple' : template?.type === 'IncrementalTextarea' ? 'fillEntryIncremental' : 'fillEntry', {
        id: entry?._id,
        directory,
        index,
        field: field?.name,
        value
    }, { notify: true, hasFile: template?.type === 'MultiImageUpload' || template?.type === 'MultiFileUpload', callback: () => {
        message.success('Changes saved')
        reload()
    }, manual: true, load: { msg: 'Saving changes...' } })

    const [{ loading }, deleteEntryValue] = usePet('deleteEntryValue', {
        id: entry?._id,
        field: field?.name,
        index
    }, { notify: true, callback: () => {
        message.success('Value has been deleted')
        setVal(null)
        setPrev(null)
        reload()
    }, manual: true, load: { msg: 'Deleting value...' } })

    const editVal = e => setVal(e.target.value)

    useEffect(() => {
        if (commitVersion > 0 && prev !== value)
            fillEntry()
    }, [commitVersion]) // eslint-disable-line react-hooks/exhaustive-deps

    const editIncrementalVal = (e, prv, i) => {
        setIndex(i)
        setVal(e.target.value)
        setPrev(prv)
    }

    const onBlur = () => {
        if (prev !== value)
            fillEntry()
    }

    let canEdit = user?.issuperuser || !currFld?.filledBy || (currFld.filledBy?._id === user?._id) || (currFld.filledBy === user?._id)

    const newEntry = () => {
        let temp = [...filled, { name: field.name, filledBy: user }]
        setFilled(temp)
    }

    const deleteEntry = i => {
        setIndex(i)
        Confirm({
            title: 'Delete Entry Value?',
            message: 'Please note that this action is not reversible.',
            onOk: deleteEntryValue,
            okText: 'Delete Value',
            severity: 'danger'
        })
    }

    return (
        <div className={`w-full ${field.md ?? ''} px-3 mb-6 md:mb-0`}>
            {
                entry &&
                field.type.includes('Incremental') ?
                    <>
                        <div className="flex justify-between items-center">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                { field.name }
                            </label>
                            {
                                !filled.some(fld => fld?.filledBy?._id === user?._id) &&
                                <button onClick={ newEntry }>New Entry</button>
                            }
                        </div>
                        {
                            (filled?.length > 0 ? filled : [{ filledBy: user }]).map((fld, i) => {
                                let canEditThis = user?.issuperuser || !fld?.filledBy || (fld.filledBy?._id === user?._id)
                                let prevVal = fld?.value ? fld?.value[0] : undefined
    
                                return (
                                    <>
                                        { fld.filledOn && <span className="flex justify-between items-center"><span className="text-gray-500 normal-case font-normal text-xs italic"> - Filled By { fld?.filledBy?.name } on { stringifyDate(fld.filledOn) }</span>{ user?.issuperuser && <Tooltip title="Clear Entries"><span className="p-2 cursor-pointer" onClick={ () => deleteEntry(i) }><AiOutlineDelete /></span></Tooltip> }</span> }
                                        {
                                            field.type === 'IncrementalTextarea' ?
                                                <textarea
                                                    disabled={ !canEditThis }
                                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-1 leading-tight focus:outline-none focus:bg-white"
                                                    id={ `grid-first-${ field.name.toLowerCase() }` }
                                                    rows={ field.rows ?? '5' }
                                                    name={ field.name }
                                                    defaultValue={ prevVal }
                                                    onChange={ e => editIncrementalVal(e, prevVal, fld.index) }
                                                    onBlur={ onBlur }
                                                    placeholder={ field.placeholder ?? `${field.name}` } />
                                            : null
                                        }
                                    </>
                                )
                            })
                        }
                        <span className="mb-3" />
                    </>
                : 
                    <>
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                            { field.name } { currFld?.filledBy && <span className="text-gray-500 normal-case font-normal text-xs italic flex justify-between items-center"><span> - Filled By { currFld?.filledBy?.name } on { stringifyDate(currFld.filledOn) }</span>{ user?.issuperuser && <Tooltip title="Clear Entries"><span className="p-2 cursor-pointer" onClick={ () => deleteEntry(0) }><AiOutlineDelete /></span></Tooltip> }</span> }
                        </label>
                        {
                            field.type === 'Textarea' ?
                                <textarea
                                    disabled={ !canEdit }
                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                    id={`grid-first-${field.name.toLowerCase()}`}
                                    rows={field.rows ?? '5'}
                                    name={field.name}
                                    value={value}
                                    onChange={editVal}
                                    onBlur={onBlur}
                                    placeholder={field.placeholder ?? `${field.name}`} />
                                : field.type === 'DateTime' ?
                                    <Datepicker
                                        showTime
                                        disabled={ !canEdit }
                                        value={value}
                                        onChange={ setVal }
                                        onBlur={onBlur} />
                                : field.type === 'Date' ?
                                    <Datepicker
                                        disabled={ !canEdit }
                                        value={value}
                                        onChange={ setVal }
                                        onBlur={onBlur} />
                                    : field.type === 'Number' ?
                                        <input
                                            disabled={ !canEdit }
                                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                            id={`grid-first-${field.name.toLowerCase()}`}
                                            type="number"
                                            name={field.name}
                                            value={value}
                                            onChange={editVal}
                                            onBlur={onBlur}
                                            placeholder={field.placeholder ?? `${field.name}`} />
                                        : field.type === 'Select' ?
                                            <Select
                                                isDisabled={ !canEdit }
                                                className="mb-3"
                                                value={ value ? { label: value, value } : null }
                                                onBlur={onBlur}
                                                onChange={vl => setVal(vl.label)}
                                                options={field?.options?.map(option => ({ label: option, value: option })) ?? []}
                                                placeholder={field.placeholder} />
                                            : field.type === 'MultiSelect' ?
                                                <Select
                                                    isMulti
                                                    isDisabled={ !canEdit }
                                                    className="mb-3"
                                                    value={value ? value.map(v => ({ label: v, value: v })) : []}
                                                    onBlur={onBlur}
                                                    onChange={vl => setVal(vl.map(v => v.label))}
                                                    options={field?.options?.map(option => ({ label: option, value: option })) ?? []}
                                                    placeholder={field.placeholder} />
                                                : field.type === 'SearchableSelect' ?
                                                    template?.dataSourceKey === 'icd11_diagnoses' ?
                                                        <Icd11Searchable
                                                            field={field}
                                                            disabled={ !canEdit }
                                                            value={value}
                                                            onChange={setVal}
                                                            onCommit={ () => setCommitVersion(version => version + 1) } />
                                                        :
                                                        <Searchable
                                                            entry={entry}
                                                            field={field}
                                                            disabled={ !canEdit }
                                                            template={template}
                                                            onChange={setVal}
                                                            onBlur={onBlur}
                                                            value={value}
                                                            placeholder={field.placeholder} />
                                                    : field.type === 'MultiSearchableSelect' ?
                                                        template?.dataSourceKey === 'icd11_diagnoses' ?
                                                            <Icd11Searchable
                                                                multi
                                                                field={field}
                                                                disabled={ !canEdit }
                                                                value={value}
                                                                onChange={setVal}
                                                                onCommit={ () => setCommitVersion(version => version + 1) } />
                                                            :
                                                            <Searchable
                                                                multi
                                                                entry={entry}
                                                                field={field}
                                                                disabled={ !canEdit }
                                                                template={template}
                                                                onChange={setVal}
                                                                onBlur={onBlur}
                                                                value={value}
                                                                placeholder={field.placeholder} />
                                                        : field.type === 'SearchableCreatableSelect' ?
                                                            <SearchableCreatable
                                                                value={value}
                                                                template={template}
                                                                disabled={ !canEdit }
                                                                onChange={setVal}
                                                                onBlur={onBlur}
                                                                entry={entry}
                                                                field={field}
                                                                placeholder={field.placeholder} />
                                                            : field.type === 'MultiSearchableCreatableSelect' ?
                                                                <SearchableCreatable
                                                                    multi
                                                                    value={value}
                                                                    disabled={ !canEdit }
                                                                    template={template}
                                                                    onChange={setVal}
                                                                    onBlur={onBlur}
                                                                    entry={entry}
                                                                    field={field}
                                                                    placeholder={field.placeholder} />
                                                                : field.type === 'CreatableSelect' ?
                                                                    <CreatableSelect
                                                                        className="mb-3"
                                                                        value={value}
                                                                        disabled={ !canEdit }
                                                                        template={template}
                                                                        onChange={setVal}
                                                                        onBlur={onBlur}
                                                                        options={field?.options?.map(option => ({ label: option, value: option })) ?? []}
                                                                        placeholder={field.placeholder ?? `${field.name}`} />
                                                                    : field.type === 'SearchableFromPrevious' ?
                                                                        <SearchableFromPrevious
                                                                            className="mb-3"
                                                                            value={value}
                                                                            disabled={ !canEdit }
                                                                            template={template}
                                                                            onChange={setVal}
                                                                            onBlur={onBlur}
                                                                            entry={entry}
                                                                            field={field}
                                                                            placeholder={field.placeholder} />
                                                                        : field.type === 'MultiSearchableFromPrevious' ?
                                                                            <SearchableFromPrevious
                                                                                multi
                                                                                value={value}
                                                                                disabled={ !canEdit }
                                                                                template={template}
                                                                                onChange={setVal}
                                                                                onBlur={onBlur}
                                                                                entry={entry}
                                                                                field={field}
                                                                                placeholder={field.placeholder} />
                                                                            : field.type === 'MultiImageUpload' || field.type === 'MultiFileUpload' ?
                                                                                <MultiFileUpload
                                                                                    multi
                                                                                    disabled={ !canEdit }
                                                                                    directory={directory}
                                                                                    entry={entry}
                                                                                    field={field}
                                                                                    onChange={setVal}
                                                                                    onBlur={onBlur}
                                                                                    value={value} />
                                                                                :
                                                                                <input
                                                                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                                                                    id={`grid-first-${field.name.toLowerCase()}`}
                                                                                    type="text"
                                                                                    disabled={ !canEdit }
                                                                                    name={field.name}
                                                                                    value={value}
                                                                                    onChange={editVal}
                                                                                    onBlur={onBlur}
                                                                                    placeholder={field.placeholder ?? `${field.name}`} />
                        }
                    </>
            }
        </div>
    )
}

// Consecutive fields sharing a non-empty `group` (ISaleItemField.group, api/src/models/sales.ts)
// render as one row instead of each getting its own full-width line - e.g. group: "Visual
// Acuity" on fields named "Right Eye"/"Left Eye". Grouping is purely by array order, no
// reordering - admins control which fields end up together by placing them next to each
// other in the form builder (components/drawers/formFields.js).
export const groupFieldsIntoRows = fields => {
    const rows = []
    for (const field of fields ?? []) {
        const last = rows[rows.length - 1]
        if (field.group && last?.group === field.group) last.fields.push(field)
        else rows.push({ group: field.group, fields: [field] })
    }
    return rows
}

export const EntryFields = ({ entry, fields, reload = () => null }) =>
    groupFieldsIntoRows(fields).map(row =>
        row.fields.length > 1 ?
            <div className="w-full mb-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 px-3" htmlFor="grid-name">
                    { row.group }
                </label>
                <div className="flex flex-wrap">
                    {
                        row.fields.map(field =>
                            <div className="flex-1 min-w-0">
                                <EntryField entry={ entry } field={ field } reload={ reload } />
                            </div>)
                    }
                </div>
            </div>
        :
            <EntryField entry={ entry } field={ row.fields[0] } reload={ reload } />
    )
